import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { UserState } from '../state/UserState';

export interface ColumnPref {
  key: string;
  visible: boolean;
  order?: number;
  width?: string;
}

export interface TableColumnPrefs {
  tableId: string;
  version?: number;
  columns: ColumnPref[];
  updatedAt?: string;
}

@Injectable({ providedIn: 'root' })
export class ColumnPreferencesService {
  private prefix = 'columnPrefs';
  private store = inject(Store);

  private getUserId(): string {
    try {
      const userName = this.store.selectSnapshot(UserState.getUserName) as string;
      return userName && userName.length > 0 ? userName : 'anon';
    } catch (err) {
      console.warn('Failed to read user from store for prefs', err);
      return 'anon';
    }
  }

  private localKey(tableId: string) {
    return `${this.prefix}:${this.getUserId()}:${tableId}`;
  }

  private pageSizeLocalKey(tableId: string): string {
    return `${this.localKey(tableId)}:pageSize`;
  }

  getPageSize(tableId: string): number | null {
    try {
      const stored = localStorage.getItem(this.pageSizeLocalKey(tableId));
      if (stored === null) return null;
      const pageSize = Number(stored);
      return Number.isFinite(pageSize) && pageSize > 0 ? pageSize : null;
    } catch (err) {
      console.error('Failed to read page-size preference', err);
      return null;
    }
  }

  savePageSize(tableId: string, pageSize: number): void {
    if (!Number.isFinite(pageSize) || pageSize <= 0) return;
    try {
      localStorage.setItem(this.pageSizeLocalKey(tableId), String(pageSize));
    } catch (err) {
      console.error('Failed to save page-size preference', err);
    }
  }

  getPreferences(tableId: string): TableColumnPrefs | null {
    try {
      const raw = localStorage.getItem(this.localKey(tableId));
      if (!raw) return null;
      return JSON.parse(raw) as TableColumnPrefs;
    } catch (err) {
      console.error('Failed to read column prefs', err);
      return null;
    }
  }

  savePreferences(tableId: string, prefs: TableColumnPrefs): void {
    try {
      prefs.updatedAt = new Date().toISOString();
      localStorage.setItem(this.localKey(tableId), JSON.stringify(prefs));
    } catch (err) {
      console.error('Failed to save column prefs', err);
    }
  }
}
